import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingIndicator } from '@dhl/components'
import { Alert, Body1, Box, Button, Grid, MenuItem, Paper, Select, Switch, TextField } from '@dhl-ui/components'
import { useDebounce } from '@dhl/utils'
import { type EasySearchResultDto, useGetFlowTypes, usePidSearchHook } from '@dhl/mdp-api'

import { useCanViewPpp } from '~/modules/ppp/hooks'
import { PppResultDisplay } from '~/modules/ppp/components'
import { useNotificationMessage } from '~/common'
import { sanitizeParcelId } from '~/modules/ppp/helpers'

const minParcelIdLength = 8

export const PppPage = () => {
  const { t } = useTranslation()
  const canAccess = useCanViewPpp()
  const { showError } = useNotificationMessage()

  const getPidSearch = usePidSearchHook()
  const { data: flowsData } = useGetFlowTypes()
  const flows = flowsData?.flowTypes ?? []

  const [loading, setLoading] = useState<boolean>(false)
  const [parcelId, setParcelId] = useState<string>('')
  const [onlyErrors, setOnlyErrors] = useState<boolean>(false)
  const [flowType, setFlowType] = useState<string>('pid-only-search')
  const [result, setResult] = useState<EasySearchResultDto | undefined>(undefined)
  const debouncedParcelId = useDebounce(parcelId, 200)
  const buttonEnabled = !loading && flowType && debouncedParcelId.length >= minParcelIdLength
  const hasResultData = (result?.overview?.length ?? 0) > 0 && (result?.hits?.length ?? 0) > 0

  const fetchData = async () => {
    const sanitizedParcelId = sanitizeParcelId(debouncedParcelId)
    setLoading(true)
    setResult(undefined)
    const response = await getPidSearch({
      parcelId: sanitizedParcelId,
      flowType,
    })
    if (response) {
      setResult(response)
    } else {
      showError(t('admin.pppPage.loadError'))
    }
    setLoading(false)
  }
  return (
    <>
      {canAccess && (
        <Box>
          <Grid container direction="row" gap={1} alignItems="center">
            <Grid size={{ xs: 3 }}>
              <Select
                label={t('admin.pppPage.flowType')}
                name="flowType"
                value={flowType ?? ''}
                onChange={(event) => {
                  setFlowType(event.target.value)
                }}
                fullWidth
                data-testid="flow-type-select"
                size="large"
              >
                {flows.map((flow) => (
                  <MenuItem key={flow} value={flow}>
                    {flow}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField
                fullWidth
                name="parcelId"
                label={t('admin.pppPage.searchPlaceholder')}
                onChange={(event) => {
                  setParcelId(event.target.value)
                }}
                data-testid="parcel-id-input"
                size="large"
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Button
                onClick={fetchData}
                variant="contained"
                color="primary"
                disabled={!buttonEnabled}
                data-testid="ppp-search-button"
              >
                {t('admin.pppPage.searchButton')}
              </Button>
            </Grid>
            {hasResultData && (
              <Grid size={{ xs: 2 }} container direction="row" alignItems="center">
                <Body1>{t('admin.pppPage.onlyError')}</Body1>
                <Switch checked={onlyErrors} onChange={() => setOnlyErrors(!onlyErrors)} />
              </Grid>
            )}
          </Grid>
          {loading && <LoadingIndicator />}
          {result && (
            <>
              {hasResultData ? (
                <Paper>
                  <PppResultDisplay result={result} onlyErrors={onlyErrors} pid={debouncedParcelId ?? ''} />
                </Paper>
              ) : (
                <>
                  <Alert sx={{ my: 2 }} variant="standard" severity="error">
                    <Body1>{t('admin.pppPage.hits.noItems', { pid: parcelId })}</Body1>
                  </Alert>
                </>
              )}
            </>
          )}
        </Box>
      )}
    </>
  )
}
